import { isEqual } from "es-toolkit";
import { atom } from "jotai";
import { atomFamily, atomWithRefresh } from "jotai/utils";

import type { PageMetadata } from "@moment/api-collab/api-types";
import { Logger } from "@moment/logging";

import { $documentLocalPath } from "../document-list";
import { $applyExternalChange } from "../file-watcher/apply-external-change.atom";
import { exists, fs, readTextFile } from "../filesystem";
import { $editorState } from "../prosemirror/editor-state.atom";
import { editorStateKeyForDocsRoute } from "../prosemirror/editorStateKey";
import {
	AGENTS_MD_TEMPLATE,
	AGENTS_MD_VERSION,
	MOMENT_MANAGED_SECTION_END,
	MOMENT_MANAGED_SECTION_START,
} from "./agents-template";

const logger = new Logger("agents-md");

const VERSION_REGEX = /<!-- MOMENT_AGENTS_MD_VERSION:(\d+) -->/;

/**
 * Matches the version comment line (with optional trailing newline) anywhere in a string.
 * Used to strip the version line from content regardless of its position.
 */
const VERSION_LINE_REGEX = /<!-- MOMENT_AGENTS_MD_VERSION:\d+ -->\n?/;

const stripVersionLine = (content: string): string => content.replace(VERSION_LINE_REGEX, "");

export const PRIMARY_AGENT_INSTRUCTION_FILE = "AGENTS.md";
const LEGACY_AGENT_INSTRUCTION_FILES = ["CLAUDE.md"] as const;
export const AGENT_INSTRUCTION_FILES = [
	PRIMARY_AGENT_INSTRUCTION_FILE,
	...LEGACY_AGENT_INSTRUCTION_FILES,
] as const;
type AgentInstructionFilename = (typeof AGENT_INSTRUCTION_FILES)[number];

const PRIMARY_AGENT_INSTRUCTION_FILE_LOWER = PRIMARY_AGENT_INSTRUCTION_FILE.toLowerCase();

const agentInstructionFilePath = (
	documentPath: string,
	filename: AgentInstructionFilename
): string => `${documentPath}/${filename}`;

export const primaryAgentInstructionFilePath = (documentPath: string): string =>
	agentInstructionFilePath(documentPath, PRIMARY_AGENT_INSTRUCTION_FILE);

export const agentInstructionFilePaths = (documentPath: string): string[] =>
	AGENT_INSTRUCTION_FILES.map((filename) => agentInstructionFilePath(documentPath, filename));

export const agentInstructionPersistFilePaths = (
	documentPath: string,
	currentFilePath: string
): string[] =>
	AGENT_INSTRUCTION_FILES.some(
		(filename) => currentFilePath === agentInstructionFilePath(documentPath, filename)
	)
		? agentInstructionFilePaths(documentPath)
		: [currentFilePath];

const resolveAgentInstructionSourceContent = async (
	documentPath: string
): Promise<string | null> => {
	for (const filename of AGENT_INSTRUCTION_FILES) {
		const filePath = agentInstructionFilePath(documentPath, filename);
		if (await exists(filePath)) {
			return readTextFile(filePath);
		}
	}

	return null;
};

/**
 * Filenames (case-insensitive) that are considered agent instruction files.
 */
const AGENT_INSTRUCTION_FILENAMES = new Set(AGENT_INSTRUCTION_FILES.map((f) => f.toLowerCase()));

/**
 * Check if a filename (case-insensitive) is one of our reserved agent instruction files.
 */
export const isAgentInstructionFilename = (filename?: string): boolean => {
	if (!filename) {
		return false;
	}

	return AGENT_INSTRUCTION_FILENAMES.has(filename.toLowerCase());
};

/**
 * Check if a page path (relative to document root) points to a root-level
 * agent instruction file such as `AGENTS.md` or `CLAUDE.md`.
 *
 * This is intentionally restricted to document-root paths so nested pages like
 * `guides/AGENTS.md` continue to behave like normal markdown pages.
 */
export const isAgentInstructionPage = (pagePath?: string): boolean => {
	if (!pagePath) {
		return false;
	}

	const normalized = pagePath.toLowerCase().replace(/^\.\//, "");
	if (normalized.includes("/")) {
		return false;
	}

	return isAgentInstructionFilename(normalized);
};

export const isPrimaryAgentInstructionPage = (pagePath?: string): boolean => {
	if (!pagePath) {
		return false;
	}

	const lower = pagePath.toLowerCase().replace(/^\.\//, "");
	return lower === PRIMARY_AGENT_INSTRUCTION_FILE_LOWER;
};

/**
 * Check if file content has the Moment-managed version marker.
 */
export const hasMomentVersionMarker = (content: string): boolean => {
	return VERSION_REGEX.test(content);
};

const SECTION_READONLY_OPEN_TAG_RE = /<section\s+readonly[^>]*>/i;

/**
 * Detects a legacy (pre-v17) Moment-managed AGENTS.md/CLAUDE.md file: the
 * file has a top-level `MOMENT_AGENTS_MD_VERSION` marker (not wrapped in a
 * `<section readonly>`), and that version is older than the current template.
 * Pre-v17 files had no readonly wrapper, so the entire body is template
 * content — no user-authored content to preserve.
 */
export const isLegacyUnwrappedTemplate = (content: string): boolean => {
	if (SECTION_READONLY_OPEN_TAG_RE.test(content)) {
		return false;
	}
	const match = content.match(VERSION_REGEX);
	if (!match) {
		return false;
	}
	return parseInt(match[1], 10) < AGENTS_MD_VERSION;
};

/**
 * Wraps pre-v17 template content in a `<section readonly>` so the editor
 * hides it as a managed section. The raw content is preserved inside the
 * section; the banner's Update action will replace the whole thing with
 * the current template.
 */
export const wrapLegacyTemplate = (content: string): string =>
	`<section readonly data-variant="moment-agents-template">\n${content}\n</section>\n`;

/**
 * Check if file content has the Moment-managed section markers.
 */
export const hasManagedSection = (content: string): boolean => {
	return (
		content.includes(MOMENT_MANAGED_SECTION_START) &&
		content.includes(MOMENT_MANAGED_SECTION_END)
	);
};

/**
 * Extract the managed section from a file, including the markers.
 */
export const extractManagedSection = (content: string): string | null => {
	const startIdx = content.indexOf(MOMENT_MANAGED_SECTION_START);
	const endIdx = content.indexOf(MOMENT_MANAGED_SECTION_END);
	if (startIdx === -1 || endIdx === -1) {
		return null;
	}
	return content.slice(startIdx, endIdx + MOMENT_MANAGED_SECTION_END.length);
};

/**
 * Regex to detect a <section readonly ...> opening tag at the end of a string.
 */
const SECTION_READONLY_OPEN_RE = /<section\s+readonly[^>]*>\s*$/;

/**
 * Regex to detect a </section> closing tag at the start of a string.
 */
const SECTION_READONLY_CLOSE_RE = /^\s*<\/section>/;

/**
 * Extract user-authored content (everything outside the managed section).
 * For legacy files (no markers but has version comment), returns empty string.
 * For user-authored files (no markers, no version), returns the full content.
 */
export const extractUserContent = (content: string): string => {
	if (!hasManagedSection(content)) {
		if (hasMomentVersionMarker(content)) {
			return "";
		}
		return content;
	}

	const startIdx = content.indexOf(MOMENT_MANAGED_SECTION_START);
	const endIdx = content.indexOf(MOMENT_MANAGED_SECTION_END) + MOMENT_MANAGED_SECTION_END.length;

	let before = content.slice(0, startIdx);
	let after = content.slice(endIdx);

	// Strip the <section readonly> wrapper if present around the managed section.
	const beforeNoVersion = stripVersionLine(before);
	const openMatch = beforeNoVersion.match(SECTION_READONLY_OPEN_RE);
	const closeMatch = after.match(SECTION_READONLY_CLOSE_RE);
	if (openMatch && closeMatch) {
		before = beforeNoVersion.slice(0, openMatch.index);
		after = after.slice(closeMatch[0].length);
	} else {
		before = beforeNoVersion;
	}

	return (before + after).trim();
};

/**
 * Merge the Moment-managed template with user content. The managed section goes
 * at the top, and user content follows after a blank line separator.
 */
export const mergeAgentsMdContent = (userContent: string): string => {
	if (!userContent.trim()) {
		return AGENTS_MD_TEMPLATE;
	}
	return AGENTS_MD_TEMPLATE + "\n" + userContent.trim() + "\n";
};

/**
 * Replace just the managed section in an existing file, preserving user content.
 */
export const replaceManagedSection = (existingContent: string): string => {
	const userContent = extractUserContent(existingContent);
	return mergeAgentsMdContent(userContent);
};

export type AgentsMdVersionResult =
	| { status: "unavailable" }
	| { status: "not-found" }
	| { status: "found"; version: number; hasManagedSection: boolean };

const $agentsMdVersionFamily = atomFamily(
	(params: { documentId: string }) =>
		atomWithRefresh(async (get): Promise<AgentsMdVersionResult> => {
			const documentPath = await get($documentLocalPath(params.documentId));

			if (!documentPath) {
				return { status: "unavailable" };
			}

			try {
				const primaryPath = agentInstructionFilePath(
					documentPath,
					PRIMARY_AGENT_INSTRUCTION_FILE
				);
				const fileExists = await exists(primaryPath);
				if (!fileExists) {
					return { status: "not-found" };
				}

				const content = await readTextFile(primaryPath);
				const match = content.match(VERSION_REGEX);
				return {
					status: "found",
					version: match ? parseInt(match[1], 10) : 0,
					hasManagedSection: hasManagedSection(content),
				};
			} catch (error) {
				logger.error("Failed to read agent instruction files version", {
					error,
					documentPath,
				});
				return { status: "unavailable" };
			}
		}),
	isEqual
);

export const $agentsMdVersion = (params: { documentId: string }) => {
	const innerAtom = $agentsMdVersionFamily(params);
	innerAtom.debugLabel = `agentsMdVersion/${params.documentId}`;
	return innerAtom;
};

export { AGENTS_MD_VERSION };

export type AgentInstructionFileInfo = {
	filename: string;
	filePath: string;
	/** Deterministic page ID derived from the filename. */
	pageId: string;
	/** Synthetic PageMetadata for routing and editor integration. */
	pageMetadata: PageMetadata;
};

/**
 * Stable page ID for the primary agent instruction file (AGENTS.md).
 * Used as the Reboot PageAuthority ID for collab editing and as the
 * editor state key for routing/display.
 * Only root-level AGENTS.md uses this — nested ones are normal pages.
 *
 * Uses alphanumeric format (no underscores/special chars) to be
 * compatible with Reboot state ID constraints.
 */
export const PRIMARY_AGENTS_MD_PAGE_ID = "momentagentsmd";

/**
 * Deterministic page ID for an agent instruction file.
 * For the primary file (AGENTS.md), returns the Reboot-compatible
 * PRIMARY_AGENTS_MD_PAGE_ID. For legacy files (CLAUDE.md), returns
 * a double-underscore-wrapped ID.
 */
export const agentInstructionPageId = (filename: string): string => {
	if (filename.toLowerCase() === PRIMARY_AGENT_INSTRUCTION_FILE_LOWER) {
		return PRIMARY_AGENTS_MD_PAGE_ID;
	}
	return `__${filename.replace(".", "_").toLowerCase()}__`;
};

/**
 * Returns the list of agent instruction files (AGENTS.md, CLAUDE.md) that
 * exist on disk for the given document. Used by the Misc sidebar section
 * and by the slug resolver to support navigation to these files.
 *
 * Each entry includes a synthetic PageMetadata so these files can participate
 * in routing and editor state loading without being stored in moment.yml.
 */
const $agentInstructionFilesFamily = atomFamily(
	(params: { documentId: string }) =>
		atomWithRefresh(async (get): Promise<AgentInstructionFileInfo[]> => {
			const documentPath = await get($documentLocalPath(params.documentId));

			if (!documentPath) {
				return [];
			}

			try {
				const results = await Promise.all(
					AGENT_INSTRUCTION_FILES.map(async (filename) => {
						const filePath = `${documentPath}/${filename}`;
						const fileExists = await exists(filePath);
						if (!fileExists) {
							return null;
						}
						const pageId = agentInstructionPageId(filename);
						const pageMetadata: PageMetadata = {
							id: pageId,
							slug: filename,
							title: filename,
							path: filename,
							type: "markdown",
							archived: false,
							parentPageID: null,
						};
						return { filename, filePath, pageId, pageMetadata };
					})
				);
				return results.filter((r): r is NonNullable<typeof r> => r !== null);
			} catch (error) {
				logger.error("Failed to detect agent instruction files", {
					error,
					documentPath,
				});
				return [];
			}
		}),
	isEqual
);

export const $agentInstructionFiles = (params: { documentId: string }) => {
	const innerAtom = $agentInstructionFilesFamily(params);
	innerAtom.debugLabel = `agentInstructionFiles/${params.documentId}`;
	return innerAtom;
};

const $agentsMdSuggestionDismissedInternal = atomFamily(
	(_documentId: string) => atom(false),
	isEqual
);

const $agentsMdSuggestionDismissedFamily = atomFamily(
	(documentId: string) =>
		atom(
			(get) => get($agentsMdSuggestionDismissedInternal(documentId)),
			(_get, set, dismissed: boolean) => {
				set($agentsMdSuggestionDismissedInternal(documentId), dismissed);
			}
		),
	isEqual
);

export const $agentsMdSuggestionDismissed = (documentId: string) => {
	const innerAtom = $agentsMdSuggestionDismissedFamily(documentId);
	innerAtom.debugLabel = `agentsMdSuggestionDismissed/${documentId}`;
	return innerAtom;
};

export const $ensurePrimaryAgentInstructionFile = atom(
	null,
	async (
		get,
		set,
		params: { documentId: string }
	): Promise<"created" | "already-exists" | "no-legacy-file" | "unavailable" | "failed"> => {
		const documentPath = await get($documentLocalPath(params.documentId));

		if (!documentPath) {
			logger.debug("Cannot ensure AGENTS.md: document path not available", {
				documentId: params.documentId,
			});
			return "unavailable";
		}

		try {
			const agentsMdPath = agentInstructionFilePath(
				documentPath,
				PRIMARY_AGENT_INSTRUCTION_FILE
			);
			if (await exists(agentsMdPath)) {
				return "already-exists";
			}

			for (const filename of LEGACY_AGENT_INSTRUCTION_FILES) {
				const legacyPath = agentInstructionFilePath(documentPath, filename);
				if (!(await exists(legacyPath))) {
					continue;
				}

				const legacyContent = await readTextFile(legacyPath);
				const fsApi = fs();
				await set(fsApi.writeTextFile, {
					filePath: agentsMdPath,
					content: legacyContent,
					documentId: params.documentId,
				});
				logger.info("Created AGENTS.md from legacy agent instruction file", {
					documentId: params.documentId,
					sourceFile: filename,
				});

				set($agentInstructionFilesFamily({ documentId: params.documentId }));
				set($agentsMdVersionFamily({ documentId: params.documentId }));

				return "created";
			}

			return "no-legacy-file";
		} catch (error) {
			logger.error("Failed to ensure primary agent instruction file", {
				error,
				documentId: params.documentId,
			});
			return "failed";
		}
	}
);
$ensurePrimaryAgentInstructionFile.debugLabel = "ensurePrimaryAgentInstructionFile";

export const $createAgentsMd = atom(null, async (get, set, params: { documentId: string }) => {
	const documentPath = await get($documentLocalPath(params.documentId));

	if (!documentPath) {
		logger.error("Cannot create agent instruction files: document path not available");
		return false;
	}

	try {
		const fsApi = fs();
		await Promise.all(
			agentInstructionFilePaths(documentPath).map((filePath) =>
				set(fsApi.writeTextFile, {
					filePath,
					content: AGENTS_MD_TEMPLATE,
					documentId: params.documentId,
				})
			)
		);
		logger.debug("Created agent instruction files", {
			documentPath,
			files: AGENT_INSTRUCTION_FILES,
		});

		set($agentsMdSuggestionDismissed(params.documentId), true);
		set($agentInstructionFilesFamily({ documentId: params.documentId }));
		set($agentsMdVersionFamily({ documentId: params.documentId }));

		return true;
	} catch (error) {
		logger.error("Failed to create agent instruction files", { error, documentPath });
		return false;
	}
});
$createAgentsMd.debugLabel = "createAgentsMd";

/**
 * Update AGENTS.md/CLAUDE.md preserving user-authored content.
 * - If file has managed section markers: replace only the managed section.
 * - If file has version marker but no managed markers (legacy): replace entire file.
 * - If file has no markers at all (user-authored): prepend managed template.
 */
export const $updateAgentsMd = atom(null, async (get, set, params: { documentId: string }) => {
	const documentPath = await get($documentLocalPath(params.documentId));

	if (!documentPath) {
		logger.error("Cannot update agent instruction files: document path not available");
		return false;
	}

	try {
		const fsApi = fs();
		const sourceContent = await resolveAgentInstructionSourceContent(documentPath);
		const newContent =
			sourceContent !== null ? replaceManagedSection(sourceContent) : AGENTS_MD_TEMPLATE;

		await Promise.all(
			agentInstructionFilePaths(documentPath).map((filePath) =>
				set(fsApi.writeTextFile, {
					filePath,
					content: newContent,
					documentId: params.documentId,
				})
			)
		);
		logger.debug("Updated agent instruction files", {
			documentPath,
			files: AGENT_INSTRUCTION_FILES,
		});

		// write_text_file suppresses the file watcher for our own writes, so the
		// ProseMirror editor won't see the new content via $fileWatcherEffect.
		// Push the new content into any loaded agent-instruction editor states so
		// the doc picks up the readonly_section node — otherwise useHasManagedSection
		// keeps returning false and the banner disappears after "Update".
		for (const filename of AGENT_INSTRUCTION_FILES) {
			const pageId = agentInstructionPageId(filename);
			const editorKey = editorStateKeyForDocsRoute({
				documentId: params.documentId,
				pageId,
			});
			if (get($editorState(editorKey)) !== null) {
				set($applyExternalChange, {
					documentId: params.documentId,
					pageId,
					content: newContent,
					skipSuggestions: true,
				});
			}
		}

		set($agentsMdSuggestionDismissed(params.documentId), true);
		set($agentInstructionFilesFamily({ documentId: params.documentId }));
		set($agentsMdVersionFamily({ documentId: params.documentId }));

		return true;
	} catch (error) {
		logger.error("Failed to update agent instruction files", { error, documentPath });
		return false;
	}
});
$updateAgentsMd.debugLabel = "updateAgentsMd";
